import { Link } from 'react-router-dom';
import { ArrowRight, Gauge, MapPinned, Sparkles, UsersRound } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { AI_TOOLS } from '../../plans';
import { Badge, Button, EmptyState, MetricCard, SectionHeader, Skeleton, StatChange } from '../../components/ui';
import { buildMetrics, buildRecommendations } from './insights';
import './dashboard.css';

const PRIORITY_TONE = { high: 'danger', medium: 'warning', low: 'info' };

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function Overview() {
  const { user } = useAuth();
  const { workspace, loading } = useWorkspace();

  if (loading) {
    return (
      <div className="dt-stack-5">
        <Skeleton height={64} />
        <div className="dt-metric-grid">{Array.from({ length: 4 }, (_, index) => <Skeleton key={index} height={110} />)}</div>
        <Skeleton height={220} />
      </div>
    );
  }

  const metrics = buildMetrics(workspace);
  const recommendations = buildRecommendations(workspace);
  const businessName = workspace.optimization?.business || user?.name?.split(' ')[0] || 'there';
  const recentDrafts = (workspace.generations || []).slice(0, 4);

  return (
    <div className="dt-stack-5">
      <header className="overview-greeting">
        <h1 className="dt-title">{greeting()}, {businessName}</h1>
        <p className="dt-support">Here is what is happening with your business right now.</p>
      </header>

      <section aria-label="Business metrics">
        <div className="dt-metric-grid">
          <MetricCard
            label="Search visibility" icon={Gauge} to="/dashboard/visibility"
            help="Your latest website scan score across seven local-search signals."
            value={metrics.visibility.value} unit={metrics.visibility.unit}
            change={<StatChange value={metrics.visibility.change} label={metrics.visibility.change !== null ? 'vs last scan' : undefined} />}
            footer={metrics.visibility.footer}
          />
          <MetricCard
            label="Leads" icon={UsersRound} to="/dashboard/leads"
            help="Every inquiry saved to your pipeline, grouped by stage."
            value={metrics.leads.value} footer={metrics.leads.footer}
          />
          <MetricCard
            label="Average position" icon={MapPinned} to="/dashboard/rank"
            help="The average of the best recorded position across your tracked keywords."
            value={metrics.rank.value} footer={metrics.rank.footer}
          />
          <MetricCard
            label="AI drafts" icon={Sparkles} to="/dashboard/ai-studio"
            help="Drafts created this month against your plan allowance."
            value={metrics.ai.value} unit={metrics.ai.unit} footer={metrics.ai.footer}
          />
        </div>
      </section>

      <section className="dt-stack-3" aria-label="Recommended actions">
        <SectionHeader
          title="Recommended actions"
          description={recommendations.length
            ? `${recommendations.length} ${recommendations.length === 1 ? 'opportunity needs' : 'opportunities need'} your attention`
            : 'You are up to date'}
        />
        {recommendations.length ? (
          <ul className="action-list">
            {recommendations.map(item => (
              <li className="action-card" key={item.id}>
                <div className="action-card-copy">
                  <div className="action-card-title">
                    <Badge tone={PRIORITY_TONE[item.priority]}>{item.priority} priority</Badge>
                    <h3 className="dt-card-title">{item.title}</h3>
                  </div>
                  <p className="dt-support">{item.description}</p>
                </div>
                <Button to={item.cta.to} variant="secondary" size="sm">{item.cta.label}<ArrowRight size={14} /></Button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="dt-card">
            <EmptyState
              title="Nothing needs attention"
              description="Your scans, leads, keywords, and setup are all current. Keep building with AI Studio."
              actions={<Button to="/dashboard/ai-studio">Open AI Studio</Button>}
            />
          </div>
        )}
      </section>

      <section className="dt-stack-3" aria-label="Recent drafts">
        <SectionHeader title="Recent drafts" description="Pick up where you left off." actions={<Link className="dt-btn is-link" to="/dashboard/ai-studio">View all</Link>} />
        {recentDrafts.length ? (
          <ul className="recent-list">
            {recentDrafts.map(item => {
              const tool = AI_TOOLS.find(candidate => candidate.id === item.tool_key);
              return (
                <li key={item.id}>
                  <Link to={`/dashboard/ai-studio/${item.tool_key}`}>
                    <span className="recent-list-name">{tool?.name || item.tool_key}</span>
                    <span className="dt-support">{new Date(item.created_at).toLocaleDateString()}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="dt-card">
            <EmptyState
              icon={Sparkles}
              title="No drafts yet"
              description="AI Studio turns your business details into websites, campaigns, and follow-up messages you can edit."
              actions={<Button to="/dashboard/ai-studio">Create your first draft</Button>}
            />
          </div>
        )}
      </section>
    </div>
  );
}
